import { TIME_WINDOW_MODAL_WIDTH } from '@/ui/components/modals/constants';
import { defineComponent, h, ref, onMounted, computed, Ref } from 'vue';
import { Modal } from '@/ui/components/Modal';
import { button, column, loadingButton, apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { useTask } from '@/ui/concerns';
import { getTimeSettings, updateTimeWindowSettings } from '@/services/api/auth';
import { getUserTimeSettings, updateUserTimeWindowSettings } from '@/services/api/resources/user';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { Tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { ScaleType, TimeWindow, TimeWindowLabelKeys, ScaleMinutes, timeWindowValues } from '@/entities/auth/reminderConfiguration';
import { toasted } from '@/ui/helpers';
import { IdProps } from '@/ui/helpers/uiHelper';
import { useTimeWindowBox } from '@/ui/pages/notification/useTimeWindowBox';
import { v4 as uuidv4 } from 'uuid';

interface TimeWindowModalProps extends IdProps {
  active: Ref<boolean>;
}

export const TimeWindowModal = defineComponent<TimeWindowModalProps>({
  name: 'TimeWindowModal',
  props: ['id', 'active'],
  setup({ id, active }, context) {
    const timeSettings = ref<TimeWindow[]>([]);
    let recordVersion = '';
    const scaleOptions = [
      {
        value: ScaleType.minute,
        label: t('checklist.reminder-notification.minute'),
      },
      {
        value: ScaleType.hour,
        label: t('checklist.reminder-notification.hour'),
      },
      {
        value: ScaleType.day,
        label: t('checklist.reminder-notification.day'),
      },
      {
        value: ScaleType.week,
        label: t('checklist.reminder-notification.week'),
      },
    ];

    const closeModal = () => {
      context.emit('close');
    };

    const isDisabled = computed(() => timeSettings.value.some(setting => hasError(setting)));

    const hasError = (setting : TimeWindow) => {
      return timeWindowValues.some(timeWindow => setting.data.value && setting.data.from === timeWindow.from && setting.data.to === timeWindow.to
        && setting.data.value * ScaleMinutes[setting.data.scale] > timeWindow.max_minutes);
    };

    const errorMessage = (setting : TimeWindow) => {
      let message = null;
      timeWindowValues.forEach(timeWindow => {
        if (hasError(setting)) {
          message = t('reminder-notification.validation-error', {
            value: t(`reminder-notification.max_time_${timeWindow.max_time}`),
          });
        }
      });
      return message ? h('div', { class: 'reminder-configurations__error' }, [h('p', { class: 'reminder-configurations__error-message' }, [message])]) : [];
    };

    const getTimeSettingsTask = useTask(
      () => getTimeSettings(),
      response => {
        timeSettings.value = response.data;
      },
    );

    const getUserTimeSettingsTask = useTask(
      () => getUserTimeSettings(id),
      ({ data, version }) => {
        timeSettings.value = data.data;
        recordVersion = version;
      },
    );

    const {
      run: updateTimeWindowSettingsTask,
      state: updateTimeWindowSettingsState,
    } = useTask(
      () => id ? updateUserTimeWindowSettings(timeSettings.value, id, recordVersion)
        : updateTimeWindowSettings(timeSettings.value),
      () => {
        toasted.success(t('profile.notifications-updated'));
        closeModal();
      },
      uuidv4(),
      error => {
        toasted.error(error.message, { singleton: true });
      },
    );

    onMounted(() => {
      id ? getUserTimeSettingsTask.run() : getTimeSettingsTask.run();
    });

    return function render() {
      return h(Modal, {
        props: {
          active: active.value,
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
          width: TIME_WINDOW_MODAL_WIDTH,
        },
        class: 'activity-modal',
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', {
            class: 'reminder-configurations__actions',
          }, [
            button(t('common.cancel'),
              closeModal,
              undefined,
              {
                type: 'is-text',
              }),
            loadingButton(
              t('common.save'),
              updateTimeWindowSettingsTask,
              updateTimeWindowSettingsState.value.pending,
              {},
              isDisabled.value,
            ),
          ]),
        },
      }, [
        h('h2', { class: 'title' }, t('reminder-notification.time-window-title')),
        h(ResourceTable, {
          class: 'reminder-configurations__table',
          props: {
            list: timeSettings.value,
            clickableTitle: false,
            sortableTitle: false,
            isHoverable: false,
          },
          scopedSlots: {
            default: () => [
              column({
                label: 'Deadline',
                sortable: false,
              }, {
                default: ({ row: setting }: { row: TimeWindow }) => t(TimeWindowLabelKeys[setting.data.from]),
                header: () => h('div', { class: 'page-title-tooltiped' }, [
                  t('common.deadline'),
                  h(Tooltip, {
                    props: {
                      label: t('reminder-notification.tooltips.deadline'),
                      classes: 'reminder-configurations__tooltip',
                      multilined: true,
                      position: 'is-right',
                    },
                    slot: 'header',
                  }, [
                    featureExplanationIcon(),
                  ]),
                ]),
              }),

              column({
                label: 'Time before deadline',
                sortable: false,
              }, {
                default: ({ row: setting }: { row: TimeWindow }) => h('div',
                  [
                    ...useTimeWindowBox(setting).formFields(scaleOptions),
                    errorMessage(setting),

                  ]),
                header: () => h('div', { class: 'page-title-tooltiped' }, [
                  t('reminder-notification.time-before-deadline'),
                  h(Tooltip, {
                    props: {
                      label: t('reminder-notification.tooltips.time-before-deadline'),
                      classes: 'reminder-configurations__tooltip',
                      multilined: true,
                      position: 'is-left',
                    },
                    slot: 'header',
                  }, [
                    featureExplanationIcon(),
                  ]),
                ]),
              }),
            ],
          },
        }),

        apiErrorMessage(updateTimeWindowSettingsState.value),
      ]);
    };
  },
});
